import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import React from "react";
import { useSession } from "users/__session/session-context";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { ArchivedFieldHistoryButton } from "work/proposals/_views/archive/archived-field-history";
import downloadFile from "_shared/_utilities/download-file";
import { useLazyDownloadDocumentQuery } from "documents/_api/document-api";
import DocumentListColumn from "work/work-agreements/contracts/_views/document-list-column";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import { Masked } from "work/masked-fields";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  workPaneNarrowerThan,
  workPaneStretchedProseInset,
} from "work/_views/work-pane-layout";

const ListContainer = styled(Grid)(() => ({
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "20rem",
  width: "100%",
}));
const DocumentList = styled(DocumentListReadonly)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  // No left margin: the table meets the divider between the two columns. `HeadControls` above it
  // already carries the prose inset — see `workPaneProseInset`.
  overflowY: "auto",
}));
const HeadControls = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  // Both sides inset, and neither on a phone, where the row lines up on the pane's gutter (#5907).
  ...workPaneStretchedProseInset(theme),
}));
const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: "1.5em",
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type PoliciesTabProps = {
  proposal: Proposal;
};

export default function PoliciesTab(props: Readonly<PoliciesTabProps>) {
  const { proposal } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const session = useSession();
  const [downloadDocument] = useLazyDownloadDocumentQuery();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const policyDocument = await downloadDocument({ documentId: documentId.value }).unwrap();
      await downloadFile(policyDocument);
    } catch (err) {
      console.error(err);
      showErrorSnackbar(err, { doing: "downloading the policy document", operation: "proposal.policies.download" });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  const selectedDocuments = session.context?.viewingAsVendor ? proposal.vendorPolicies : proposal.clientPolicies;

  if (Masked.isMasked(selectedDocuments)) {
    return maskedProposalFieldNotice(MaskedProposalField.Policies);
  }
  return (
    <>
      <HeadControls>
        {selectedDocuments.length === 0 ? <HelperText>No policy documents</HelperText> : <span />}
        {/* The shown party's policies, every document added, replaced or removed. */}
        <ArchivedFieldHistoryButton
          field={session.context?.viewingAsVendor ? ProposalField.VendorPolicies : ProposalField.ClientPolicies}
          toolTip="View policies change history"
        />
      </HeadControls>
      <ListContainer container direction="row">
        <DocumentListColumn>
          {selectedDocuments.length > 0 && (
            <DocumentList
              documents={selectedDocuments}
              documentType={
                session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
              }
              downloadingFileId={isDownloadingFile}
              onDownload={handleDownloadDocumentById}
            />
          )}
        </DocumentListColumn>
      </ListContainer>
    </>
  );
}
